import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { SubmitButton } from "components/common/buttons/SubmitButton";
import { fetchLanguages } from "slices/languageSlice";
import { languageSelector } from "slices/languageSlice";
import { setError } from "slices/dashboardSlice";
import { AsyncPaginate } from 'react-select-async-paginate';
import { selectCustomStyles } from 'components/common/Select';
import FullPageLoader from "components/common/FullPageLoader";
import ListTable from "components/interpreterAccess/dashboard/ListTable";
import { InterpreterDashboardSelector } from "slices/interpreter/dashboardSlice";
import { fetchInterpreterDashboardAppointments } from "slices/interpreter/dashboardSlice";
import { LegendButton } from "components/common/TableColor";
import { interpreterLegends } from "components/common/TableColor";
import { fetchInterpreterDashboardAnalytics } from "slices/interpreter/dashboardSlice";
import { fetchJobLocations } from "slices/jobLocationSlice";


const DatesInputs = (({ control, errors }) => {
  const startDateRef = React.useRef(null);
  const endDateRef = React.useRef(null);

  return (
    <>
      <div className="row-primary">
        <h2>Start Date*</h2>
        <Controller
          name="start_date"
          control={control}
          render={({ field }) =>
            <div className="input-image">
              <input ref={startDateRef} id='start_date' type="date" name='start_date' placeholder='Start Date'
                {...field}
                value={moment(field.value).format('YYYY-MM-DD')}
                onChange={(e) => {
                  const dateString = moment(e.target.value).format("YYYY-MM-DD")
                  field.onChange(dateString)
                }}
              />
              <img src="/images/celender.png" alt="celender Icon" className="custom-icon" />

            </div>
          } />
      </div>
      <div className="row-primary">
        <h2>End Date*</h2>
        <Controller
          name="end_date"
          control={control}
          render={({ field }) =>
            <div className="input-image ">
              <input ref={endDateRef} type="date" name='end_date' placeholder='End Date'
                {...field}
                value={moment(field.value).format('YYYY-MM-DD')}
                onChange={(e) => {
                  const dateString = moment(e.target.value).format("YYYY-MM-DD")
                  field.onChange(dateString)
                }}
              />
              <img src="/images/celender.png" alt="celender Icon" className="custom-icon" />
            </div>
          } />
      </div>
    </>
  )
});

function Dashboard() {
  const dispatch = useDispatch()
  const { interpreterDashboard: dashboard, last_page: totalPages, loader, total } = useSelector(InterpreterDashboardSelector);
  const { language: languages } = useSelector(languageSelector);
  const [activeLegend, setActiveLegend] = useState(0);
  const [statusValue, setStatusValue] = React.useState('all');
  const [filterJobLocation, setFilterJobLocation] = useState(null);
  const defaultValues = {
    status: 'all',
    start_date: moment().format('YYYY-MM-DD'),
    end_date: moment().format('YYYY-MM-DD'),
    case_number: '',
  };
  const [filters, setFilters] = useState(defaultValues);
  const { clearErrors, handleSubmit, watch, formState: { errors }, control, setValue, setError: setErrors, reset, } = useForm({
    defaultValues: defaultValues
  });


  useEffect(() => {
    dispatch(fetchInterpreterDashboardAnalytics())
    dispatch(fetchInterpreterDashboardAppointments(1, { pageLength: 100, ...defaultValues, sortBy: "datetime", sortDirection: "asc" }))
  }, [])

  // #region JonLocations dropdown 

  const filteredJobLocationLoadOptions = async (inputValue, loadedOptions, { page }) => {
    const result = await dispatch(
      fetchJobLocations(page, { pageLength: 20, sortBy: "id", sortDirection: "desc", search: inputValue, }, true, true)
    );
    return {
      options: result?.data?.map((val) => ({
        value: val.id,
        label: val.name,
      })),

      hasMore: page !== result?.last_page,
      additional: {
        page: page + 1,
      },
    };
  };

  //#endregion

  // #region Language dropdown 
  const filteredLanguages = React.useMemo(() =>
    languages?.map(language => {
      const object = {
        value: language?.id,
        label: language?.name
      }
      return object
    }), [languages]);

  const filteredLanguagesLoadOptions = async (inputValue, loadedOptions, { page }) => {
    const result = await dispatch(fetchLanguages(page, { pageLength: 20, sortBy: "id", sortDirection: "desc", search: inputValue }, true, true))
    return {
      options: result.data.map((item) => ({
        value: item.id,
        label: item.name,
      })),
      hasMore: page !== result?.last_page ? true : false,
      additional: {
        page: page + 1,
      },
    };
  };
  //#endregion

  // #region Legends 
  const filteredStatuses = React.useMemo(() => {
    const temp_array = [...interpreterLegends];
    temp_array.unshift({
      value: "all",
      name: "All",
      id: 0,
      color: "#3176A0"
    })
    return temp_array;
  }, [interpreterLegends]);

  const hanldeActiveLegend = (id, value) => {
      setActiveLegend(id);
      setStatusValue(value);
  
      const updatedFilters = {
          ...filters,
          status: value,
          page: 1,
      };
      setFilters(updatedFilters);
  
      dispatch(fetchInterpreterDashboardAppointments(1, updatedFilters))
  }

  // const hanldeActiveLegend = (id, value) => {
  //   setActiveLegend(id);
  //   dispatch(fetchInterpreterDashboardAppointments(1, { pageLength: 10, status: value, start_date: moment(watch('start_date'))?.format('YYYY-MM-DD'), end_date: moment(watch('end_date'))?.format('YYYY-MM-DD'), sortBy: "id", sortDirection: "desc" }))
  //   setStatusValue(value)
  // }

  //#endregion


  const onSubmit = (data) => {
    dispatch(setError({}))
    data = {
      ...data,
      service: 'all',
      status: statusValue,
    }
    setFilters(data);
    dispatch(fetchInterpreterDashboardAppointments(1, data))
    clearErrors();
  };


  const handleResetFields = () => {
    setValue("start_date", moment().format('YYYY-MM-DD'));
    setValue("end_date", moment().format('YYYY-MM-DD'));
    setValue("language_id", "");
    setValue("appointment_id", "");
    setValue("job_location_id", "");
    setValue("service", "");
    setValue("case_number", "");
    const resetData = { ...defaultValues, pageLength: 100, sortBy: "datetime", sortDirection: "asc" };
    setFilters(resetData);
    dispatch(fetchInterpreterDashboardAppointments(1, resetData));
    clearErrors()
  };


  const [isFourteenDays, setIsFourteenDays] = useState(false);
  const tableRef = useRef(null);

  const handleFilter = (item, isforteen, status = 'all') => {
    const today = moment().format('YYYY-MM-DD');
    const tomorrow = moment().add(1, 'days').format('YYYY-MM-DD');
    const next_fourteen_days = moment().add(14, 'days').format('YYYY-MM-DD');

    if (isforteen == true) {
      setValue("start_date", tomorrow);
      setValue("end_date", next_fourteen_days);
    } else {
      setValue("start_date", today);
      setValue("end_date", today);
    }

    setValue("service", item?.value || null);
    setValue("status", status);
    setStatusValue(status);

    const data = {
      start_date: isforteen ? tomorrow : today,
      end_date: isforteen ? next_fourteen_days : today,
      service: item?.value || "",
      status,
    };

    dispatch(fetchInterpreterDashboardAppointments(1, data));
    tableRef.current.scrollIntoView({ behavior: "smooth" });
    dispatch(setError({}));
    clearErrors();
  };

  return (
    <>
      <div className="content">
        <div className="row_main_head">
          <h2 className="main_heading">Dashboard</h2>
        </div>

        <div className="row_dash_1">
          <div className="card_table">
            <div className="interpreter p-4">
              <h2>Today</h2>
            </div>

            <div className="Table_data_show pt-0">
              <table>
                <thead>
                  <tr>
                    <th className="col-6">Today</th>
                    <th className="col-3 text-center">Registered</th>
                    <th className="col-3 text-center">To Cover</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard?.analytics?.todays_data?.map((service, index) => (
                    <tr key={index}>
                      <td className="cursorPointer" onClick={() => { setIsFourteenDays(false); handleFilter(service, false, 'all'); }}>{service?.type}</td>
                      <td className="text-center cursorPointer" onClick={() => { setIsFourteenDays(false); handleFilter(service, false, 'registered'); }}>{service?.registered}</td>
                      <td className="text-center cursorPointer" onClick={() => { setIsFourteenDays(false); handleFilter(service, false, 'open'); }}>{service?.to_cover}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card_table">
            <div className="interpreter p-4">
              <h2>Next 14 Days</h2>
            </div>

            <div className="Table_data_show pt-0">
              <table>
                <thead>
                  <tr>
                    <th className="col-6">Today</th>
                    <th className="col-3 text-center">Registered</th>
                    <th className="col-3 text-center">To Cover</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard?.analytics?.fourteen_days?.map((service, index) => (
                    <tr key={index}>
                      <td className="cursorPointer" onClick={() => { setIsFourteenDays(true); handleFilter(service, true, 'all'); }}>{service?.type}</td>
                      <td className="text-center cursorPointer" onClick={() => { setIsFourteenDays(true); handleFilter(service, true, 'registered'); }}>{service?.registered}</td>
                      <td className="text-center cursorPointer" onClick={() => { setIsFourteenDays(true); handleFilter(service, true, 'open'); }}>{service?.to_cover}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="card_table">
          <div className="button-sm forLegends p-3">
            <LegendButton activeId={activeLegend} options={filteredStatuses} handleClick={hanldeActiveLegend} />
          </div>
          <form className="search_form_ad">
            <div className="row-primary dahboard_filter">
              <DatesInputs control={control} errors={errors} />

              <div className="row-primary">
                <h2>Job ID</h2>
                <Controller
                  name="appointment_id"
                  control={control}
                  render={({ field }) =>
                    <div className="input-basis basic_bases">
                      <input className="input-in type_inp" name="appointment_id" type="text" placeholder="job id" {...field} />
                    </div>
                  }
                />
              </div>

              <div className="row-primary">
                <h2>Case Number</h2>
                <Controller
                  name="case_number"
                  control={control}
                  render={({ field }) =>
                    <div className="input-basis basic_bases">
                      <input className="input-in type_inp" name="case_number" type="text" placeholder="e.g. 25-1-00123-1" {...field} />
                    </div>
                  }
                />
              </div>

              <div className="row-primary">
                <h2>Job Location</h2>
                <div className="w-100">
                  <Controller
                    name="job_location_id"
                    control={control}
                    render={({ field }) => (
                      <div className="w-100">
                        <AsyncPaginate
                          menuPlacement="auto"
                          styles={{
                            ...selectCustomStyles,
                          }}
                          value={filterJobLocation}
                          loadOptions={filteredJobLocationLoadOptions}
                          isClearable
                          escapeClearsValue
                          onChange={(obj) => {
                            if (obj?.value) {
                              setFilterJobLocation(obj);
                              field.onChange(obj.value);
                            } else {
                              setFilterJobLocation(null);
                              field.onChange(null);
                            }
                          }}
                          additional={{
                            page: 1,
                          }}
                        />
                      </div>
                    )}
                  />
                </div>
              </div>

              <div className="row-primary">
                <h2>Language</h2>
                <Controller
                  name="language_id"
                  control={control}
                  render={({ field }) =>
                    <div style={{ width: "100%" }}>
                      <AsyncPaginate
                        menuPlacement='auto'
                        styles={{
                          ...selectCustomStyles,
                        }}
                        value={field.value ? filteredLanguages?.find((opt) => field.value == opt.value) : ""}
                        loadOptions={filteredLanguagesLoadOptions}
                        isClearable
                        escapeClearsValue
                        onChange={(obj) => {
                          field.onChange(obj?.value);
                        }}
                        additional={{
                          page: 1,
                        }}
                      />
                    </div>
                  }
                />
              </div>
            </div>

            <div className="row-primary btns_area row_10">
              <SubmitButton handleSubmit={handleSubmit(onSubmit)} buttonType="submit" btnText="Search" className={'green'} />
              <SubmitButton buttonType="button" className={'lightBlue'} btnText="Reset" handleSubmit={handleResetFields} />
            </div>

            <div ref={tableRef}>
              <ListTable data={dashboard?.appointments} total={total} paginationParams={filters}/>
            </div>

          </form>
        </div>
      </div>

      {loader && <FullPageLoader />}
    </>
  );
}

export default Dashboard;